import Link from 'next/link'
import React, { useState } from 'react'

import { Slider } from '@/entities/card/ui/slider/slider'
import { numeralize } from '@/entities/product'

import arrowIcon from '@/shared/assets/icons/arrow.svg'
import { formatPrice } from '@/shared/lib/utils/price/format-price'
import { Button } from '@/shared/ui'
import { Icon } from '@/shared/ui/icon'

import styles from './styles.module.scss'

export type MarkdownProduct = {
	KOD: string
	PRICE: number
	MARKDOWN_CONDITION: string[] | null
	SECTION_CODE: string
	XML_ID: string
	STOCK_NAME: string
	STOCK_ID: number
	MAIN_PRODUCT_ACTIVE: string[]
	STOCK_CODE: string
	OLD_PRICE: number
	MARKDOWN_SERIES: string[]
	TITLE: string
	ID: string
	PROIZVODITEL: string
	PART_NUMBER: string
	DISCOUNT_PERCENT: string
	IMAGES?: string[]
}

type MarkdownListProps = {
	data: any
	isOpen?: boolean
	onOpenModal: (product: MarkdownProduct) => void // Новая пропа для открытия модального окна
}

export const MarkdownItem = ({ data, isOpen, onOpenModal }: MarkdownListProps): JSX.Element => {
	const {
		GROUP_NAME,
		GROUP_COUNT,
		GROUP_MIN_PRICE,
		PRODUCTS,
		IMAGES = [],
		MAIN_PRODUCT_CODE,
		GROUP_OLD_PRICE,
		GROUP_MAX_DISCOUNT_PERCENT
	} = data
	const [showList, setShowList] = useState(isOpen)
	const [isAnimating, setIsAnimating] = useState(false)

	const toggleList = () => {
		if (showList) {
			setIsAnimating(true)
			setTimeout(() => {
				setShowList(false)
				setIsAnimating(false)
			}, 300)
		} else {
			setShowList(true)
		}
	}

	return (
		<div className={styles.markdown}>
			<div
				className={styles.markdown__header}
				onClick={toggleList}
				role="button"
				tabIndex={0}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						toggleList()
					}
				}}>
				<div className={styles.markdown__imagesWrap}>
					<div
						className={`${styles.markdown__images} ${
							!showList ? styles['pagination__disabled'] : styles['pagination__active']
						}`}>
						<Slider type={`regular`} images={IMAGES.slice(0, 4)} alt={`title`} dimmed={false} />
					</div>
				</div>
				<p className={styles.markdown__title}>{GROUP_NAME}</p>

				{showList && MAIN_PRODUCT_CODE && (
					<Link
						prefetch={false}
						href={`/products/productID${MAIN_PRODUCT_CODE}`}
						className={styles.markdown__link}
						target="_blank">
						О товаре <Icon icon={arrowIcon} height={18} width={18} />
					</Link>
				)}

				<div className={styles.markdown__price}>
					<div className={styles.markdown__priceWrap}>
						от{' '}
						<div className={styles.item__price}>
							<span className={styles.item__priceValue}>
								{formatPrice(GROUP_MIN_PRICE)}
								<span className={styles.item__pricePercent}>{GROUP_MAX_DISCOUNT_PERCENT}</span>
							</span>
							{!!GROUP_OLD_PRICE && <span className={styles.item__priceOldValue}>{formatPrice(GROUP_OLD_PRICE)}</span>}
						</div>
					</div>
				</div>

				<Button theme="link" className={styles.markdown__openButton}>
					<span className={styles.markdown__quantity}>
						{GROUP_COUNT} {numeralize(GROUP_COUNT)}
					</span>
					<span
						className={styles.markdown__openButtonIcon}
						style={{
							transform: showList ? 'rotate(-90deg)' : 'rotate(90deg)'
						}}>
						<Icon icon={arrowIcon} height={24} width={24} />
					</span>
				</Button>
			</div>

			<ul
				className={`${styles.markdown__list} ${showList ? styles['markdown__list--open'] : ''}`}
				style={{ '--item-count': PRODUCTS.length } as React.CSSProperties}>
				{/*@ts-ignore*/}
				{PRODUCTS?.map(item => (
					<li
						key={item.XML_ID}
						className={`${styles.markdown__item} ${styles.item} ${!showList && isAnimating ? 'fade-out' : ''}`}>
						<div className={styles.item__condition}>
							<span className={styles.item__conditionTitle}>Состояние</span>
							<span className={styles.item__conditionValue}>{item.MARKDOWN_CONDITION}</span>
						</div>

						<div className={styles.item__location}>
							В наличии
							<Link prefetch={false} href={`/shops/${item.STOCK_ID}`} target="_blank" className={styles.item__shopLink}>
								{item.STOCK_NAME}
							</Link>
						</div>

						<div className={styles.item__price}>
							<span className={styles.item__priceValue}>
								{formatPrice(item.PRICE)}
								<span className={styles.item__pricePercent}>{item.DISCOUNT_PERCENT}</span>
							</span>
							{!!item.OLD_PRICE && <span className={styles.item__priceOldValue}>{formatPrice(item.OLD_PRICE)}</span>}
						</div>

						<div className={styles.item__button}>
							<Button theme="link" onClick={() => onOpenModal({ ...item, IMAGES })}>
								Оставить заявку
							</Button>
						</div>
					</li>
				))}
			</ul>
		</div>
	)
}
